import {AfterViewInit, Component, DestroyRef, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormControl, FormGroup} from '@angular/forms';
import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {TableLazyLoadEvent} from 'primeng/table';
import {take} from 'rxjs';
import {AlarmService} from '../../services/alarm.service';
import {DeviceService} from '../../services/device.service';
import {PageLoaderService} from '../../services/page-loader.service';
import {DialogBase} from '../../shared/components/dialogs/dialog-base/dialog-base';
import {InputSelectComponent} from '../../shared/components/input-select/input-select';
import {TableComponent} from '../../shared/components/table/table';
import {TranslateSafePipe} from '../../shared/pipes/translation.pipe';
import {formatTimeAgoTranslation} from '../../shared/utils/date.util';
import {AlarmDto, AlarmFindAndCountResponseDto, AlarmRow, AlarmSeverityCountDto} from '../../types/dtos/alarm.dto';
import {DeviceDto} from '../../types/dtos/device.dto';
import {AlarmSeverity, AlarmStatus, AlarmType} from '../../types/enums/alarm.enum';
import {InputSelectInterface, InputTextInterface} from '../../types/interfaces/input.interface';
import {TableConfig, TableRowDto} from '../../types/interfaces/table.interface';
import {DashboardBoxComponent} from '../dashboard/dashboard-box/dashboard-box.component';
import {InfoCardComponent} from '../dashboard/info-card/info-card.component';
import {DialogAlarmDetail} from './dialog-alarm-detail/dialog-alarm-detail';

@Component({
  selector: 'app-alarms',
  imports: [
    DashboardBoxComponent,
    TableComponent,
    InputSelectComponent,
    InfoCardComponent,
    DialogBase,
    DialogAlarmDetail,
    TranslateSafePipe,
  ],
  templateUrl: './alarms.component.html',
  styleUrl: './alarms.component.css',
})
export class AlarmsComponent implements AfterViewInit {
  public loading: boolean = true;
  public alarmStatusLoading: boolean = false;
  public isAlarmDetailOpen: boolean = false;
  public selectedAlarm: AlarmDto | null = null;
  public alarmsList: AlarmDto[] = [];
  public devices: DeviceDto[] = [];
  public alarmSeverityCount: AlarmSeverityCountDto | null = null;

  public filterForm: FormGroup = new FormGroup({
    severity: new FormControl(),
    alarmType: new FormControl(),
    status: new FormControl(),
    search: new FormControl(),
  });

  public searchConfig: InputTextInterface = {
    formGroup: this.filterForm,
    isTranslated: true,
    label: 'common.fields.search-default',
    id: 'search',
    iconLeft: 'search',
    showClear: true,
    type: 'text',
    onInput: () => this.loadAlarms(),
  };

  public severityConfig: InputSelectInterface = {
    formGroup: this.filterForm,
    isTranslated: true,
    label: 'common.input.severity.label',
    id: 'severity',
    iconLeft: 'priority_high',
    showClear: true,
    options: [],
    onInput: () => this.loadAlarms(),
  };

  public alarmTypeConfig: InputSelectInterface = {
    formGroup: this.filterForm,
    isTranslated: true,
    label: 'common.table.alarm-type.label',
    id: 'alarmType',
    iconLeft: 'notification_important',
    showClear: true,
    options: [],
    onInput: () => this.loadAlarms(),
  };

  public statusConfig: InputSelectInterface = {
    formGroup: this.filterForm,
    isTranslated: true,
    label: 'common.input.select.status',
    id: 'status',
    iconLeft: 'check_circle',
    showClear: true,
    options: [],
    onInput: () => this.loadAlarms(),
  };
  public tableRows: TableRowDto<AlarmRow>[] = [];
  public tableConfig: TableConfig<AlarmRow> = {
    columns: [
      {
        key: 'status',
        isTranslated: true,
        isValueTranslated: true,
        title: 'common.table.status.label',
        isTag: true,
        overrideValue: (row: AlarmRow) => 'common.enum.alarm-status.' + row.status,
        getTagColor: (row: AlarmRow) => this._alarmService.mapAlarmStatusClass(row.status),
        sticky: 'left',
      },
      {
        key: 'severity',
        isTranslated: true,
        isValueTranslated: true,
        title: 'common.table.severity.label',
        isTag: true,
        overrideValue: (row: AlarmRow) => 'common.enum.alarm-severity.' + row.severity,
        getTagColor: (row: AlarmRow) => this._alarmService.mapAlarmSeverityClass(row.severity),
      },
      {
        key: 'serial_number',
        isTranslated: true,
        title: 'common.table.serielNumber.label',
      },
      {
        key: 'message',
        isTranslated: true,
        title: 'common.table.message.label',
      },
      /*{
        key: 'measurement',
        title: 'Measurement',
      },*/
      {
        key: 'address',
        isTranslated: true,
        title: 'common.table.address.label',
      },
      {
        key: 'created_at',
        isTranslated: true,
        title: 'common.table.created-at.label',
        overrideValue: (row: AlarmRow) => formatTimeAgoTranslation(this._translateService, row.created_at),
        sticky: 'right',
      },
    ],
    mobile: {
      tags: [
        {
          key: 'status',
          isValueTranslated: true,
          overrideValue: (row: AlarmRow) => 'common.enum.alarm-status.' + row.status,
          getColor: (row: AlarmRow) => this._alarmService.mapAlarmStatusClass(row.status),
        },
        {
          key: 'severity',
          isValueTranslated: true,
          overrideValue: (row: AlarmRow) => 'common.enum.alarm-severity.' + row.severity,
          getColor: (row: AlarmRow) => this._alarmService.mapAlarmSeverityClass(row.severity),
        },
      ],
      title: {
        key: 'message'
      },
      sections: [
        {
          key: 'serial_number',
          isTranslated: true,
          title: 'common.table.serielNumber.label',
        },
        {
          key: 'created_at',
          title: 'common.table.created-at.label',
          isTranslated: true,
          isValueTranslated: true,
          overrideValue: (row: AlarmRow) => formatTimeAgoTranslation(this._translateService, row.created_at),
        },
      ]
    },
    showRows: 25,
    rowsPerPageOptions: [
      25,
      50,
      100,
    ],
    pagination: true,
    scrollHeight: '55vh',
    emptyMessage: 'common.table.empty-message.alarm',
    actions: [
      {
        icon: 'visibility',
        action: (row: AlarmRow) => {
          const alarm: AlarmDto | undefined =
            this.alarmsList.find((alarmItem: AlarmDto) =>
              alarmItem.id === row.id,
            );

          if (!alarm) {
            return;
          }

          /*this._logService.sendLog({
            target: LoggingTargetEnum.ALARM,
            action: LoggingActionEnum.VIEW,
            target_id: alarm.id,
          });*/

          this.selectedAlarm = alarm;
          this.isAlarmDetailOpen = true;
        },
      },
    ],
    lazy: true,
    onLazyLoad: (event: TableLazyLoadEvent) => {
      this.loadAlarms(
        event.rows ?? undefined,
        event.first ?? undefined,
      );
    },
  };
  private limit: number = this.tableConfig.showRows;
  private offset: number = 0;
  private readonly _destroyRef: DestroyRef = inject(DestroyRef);

  constructor(
    private readonly _alarmService: AlarmService,
    private readonly _deviceService: DeviceService,
    private readonly _route: ActivatedRoute,
    private readonly _router: Router,
    private readonly _pageLoaderService: PageLoaderService,
    private readonly _translateService: TranslateService,
  ) {
    this.severityConfig.options = Object.values(AlarmSeverity)
      .map((severity: AlarmSeverity) => ({
        isTranslated: true,
        label: 'common.enum.alarm-severity.' + severity,
        value: severity,
      }));

    this.statusConfig.options = Object.values(AlarmStatus)
      .map((status: AlarmStatus) => ({
        isTranslated: true,
        label: 'common.enum.alarm-status.' + status,
        value: status,
      }));

    this.alarmTypeConfig.options = Object.values(AlarmType)
      .map((type: AlarmType) => ({
        isTranslated: true,
        label: 'common.enum.alarm-type.' + type,
        value: type,
      }));
  }

  public get alarmDetailDialogName(): string {
    return 'common.enum.alarm-type.' + this.selectedAlarm?.alarm_type;
  }

  public get criticalAlarmCount(): number {
    return this.alarmSeverityCount?.[AlarmSeverity.CRITICAL] ?? 0;
  }

  public get highAlarmCount(): number {
    return this.alarmSeverityCount?.[AlarmSeverity.HIGH] ?? 0;
  }

  public get warningAlarmCount(): number {
    return this.alarmSeverityCount?.[AlarmSeverity.WARNING] ?? 0;
  }

  public get infoAlarmCount(): number {
    return this.alarmSeverityCount?.[AlarmSeverity.INFO] ?? 0;
  }

  public readonly searchCritical = (): void => {
    this.filterForm.patchValue({severity: AlarmSeverity.CRITICAL});
    this.loadAlarms()
      .then();
  }

  public readonly searchHigh = (): void => {
    this.filterForm.patchValue({severity: AlarmSeverity.HIGH});
    this.loadAlarms().then();
  }

  public readonly searchWarning = (): void => {
    this.filterForm.patchValue({severity: AlarmSeverity.WARNING});
    this.loadAlarms().then();

  }

  public readonly searchInfo = (): void => {
    this.filterForm.patchValue({severity: AlarmSeverity.INFO});
    this.loadAlarms().then();
  }

  public async ngAfterViewInit(): Promise<void> {
    this._alarmService.alarmSeverityCount$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((alarmSeverityCount: AlarmSeverityCountDto) => {
        this.alarmSeverityCount = alarmSeverityCount;
      });

    this._alarmService.updatedAlarm$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((alarm: AlarmDto | null) => {
        if (alarm) {

          const alarmIndex: number = this.alarmsList.findIndex(
            (currentDevice: AlarmDto) => currentDevice.id === alarm.id,
          );
          if (alarmIndex) {
            this.alarmsList[alarmIndex] = alarm;
          }
        }
      });

    await this._pageLoaderService.waitLoadingFinish();

    this._route.queryParamMap
      .pipe(take(1))
      .subscribe({
        next: (params: ParamMap) => {
          const alarmId: number = Number(params.get('id'));

          if (alarmId) {
            let unResolvedAlarms: AlarmDto[] = this._alarmService.getUnResolvedAlarms();
            let searchAlarm: AlarmDto | undefined = unResolvedAlarms.find((alarm) => alarm.id === alarmId);
            if (searchAlarm) {
              this.selectedAlarm = searchAlarm;
              this.isAlarmDetailOpen = true;
            }
          }

          const severity: AlarmSeverity | undefined = (params.get('severity')) ?
            params.get('severity') as AlarmSeverity
            : undefined;
          const alarmType: AlarmType | undefined = (params.get('alarmType')) ?
            params.get('alarmType') as AlarmType
            : undefined;

          if (severity || alarmType) {
            if (severity) {
              this.filterForm.get('severity')
                ?.setValue(severity);
            }

            if (alarmType) {
              this.filterForm.get('alarmType')
                ?.setValue(alarmType);

              this.loadAlarms();
            }
          }

        },
      });
  }

  public readonly closeAlarmDetailDialog = (): void => {
    this.isAlarmDetailOpen = false;
    this.selectedAlarm = null;
    this.alarmStatusLoading = false;
  };

  public async resetFilter(): Promise<void> {
    this.filterForm.reset();
    await this.loadAlarms();
  }

  private async loadAlarms(
    limit: number = this.limit,
    offset: number = this.offset,
  ): Promise<void> {
    this.loading = true;

    await this._pageLoaderService.waitLoadingFinish();

    const severity: AlarmSeverity | undefined = this.filterForm.get('severity')?.value
      ? this.filterForm.get('severity')?.value as AlarmSeverity
      : undefined;

    const alarm_type: AlarmType | undefined = this.filterForm.get('alarmType')?.value
      ? this.filterForm.get('alarmType')?.value as AlarmType
      : undefined;

    const status: AlarmStatus | undefined = this.filterForm.get('status')?.value
      ? this.filterForm.get('status')?.value as AlarmStatus
      : undefined;

    const rawSearch: string | undefined = this.filterForm.get('search')?.value
      ? this.filterForm.get('search')?.value as string
      : undefined;

    const search: string | undefined = rawSearch?.trim()
      ? rawSearch.trim().toLowerCase()
      : undefined;

    this._alarmService.getAll({
      limit,
      offset,
      ...(status ? {status: [status]} : {}),
      ...(severity ? {severity: [severity]} : {}),
      ...(alarm_type ? {alarm_type: [alarm_type]} : {}),
      // search,
    })
      .subscribe({
        next: (alarmResponse: AlarmFindAndCountResponseDto) => {
          const alarms: AlarmDto[] = alarmResponse.data ?? [];

          const filteredAlarms: AlarmDto[] = !search
            ? alarms
            : alarms.filter((alarm: AlarmDto) => {
              const device: DeviceDto | null = this._deviceService.getLocalById(alarm.device_id);

              const serialNumber = device?.serial_number ?? device?.device_eui ?? '';
              const address = device?.position?.address ?? '';

              return [
                alarm.message,
                alarm.severity,
                alarm.status,
                serialNumber,
                address,
              ]
                .some((value) =>
                  String(value ?? '')
                    .toLowerCase()
                    .includes(search),
                );
            });

          this.alarmsList = filteredAlarms;
          this.tableConfig.totalRecords = alarmResponse.rowCount;

          this._updateTable();

          this.limit = limit;
          this.offset = offset;
        },
        error: (error: unknown) => {
          console.error('Error fetching alarms:', error);

          this.alarmsList = [];
          this.tableRows = [];
        },
        complete: () => {
          this.loading = false;
        },
      });
  }

  private _updateTable() {
    this.tableRows = this.alarmsList.map((alarm: AlarmDto) => {
      let device: DeviceDto | null = this._deviceService.getLocalById(alarm.device_id);

      return {
        id: alarm.id,
        status: alarm.status,
        message: alarm.title,
        severity: alarm.severity,
        serial_number: device?.serial_number ?? device?.device_eui ?? '-',
        address: device?.position?.address ?? '-',
        created_at: alarm.created_at,
      };
    });
  }
}
